import { useCallback, useState } from 'react'
import { Connection, PublicKey, SystemProgram, TransactionMessage, VersionedTransaction } from '@solana/web3.js'
import { useConnect as usePhantomConnect, useIsExtensionInstalled, useModal, useSolana } from '@phantom/react-sdk'
import { parseUnits } from 'viem'
import { showApiErrorToast } from '@/lib/api-toast'
import { isSolanaNetwork } from '@/lib/payment-chains'
import { getSolanaRpcUrl } from '@/lib/solana-rpc'
import { useDepositPhantomMutation } from '@/store/api/paymentApi'
import type { Currency } from '@/types/payment'

interface UsePhantomDepositOptions {
	currency: Currency | null
	amount: string
	adminAddress: string | null
}

export function usePhantomDeposit({ currency, amount, adminAddress }: UsePhantomDepositOptions) {
	const { solana, isAvailable } = useSolana()
	const { connect: connectPhantom, isConnecting: isPhantomConnecting } = usePhantomConnect()
	const { open: openPhantomModal } = useModal()
	const { isInstalled: isPhantomExtensionInstalled, isLoading: isPhantomExtensionLoading } = useIsExtensionInstalled()
	const [depositPhantom, { isLoading: isRecordingDeposit }] = useDepositPhantomMutation()
	const [isSending, setIsSending] = useState(false)
	const [lastError, setLastError] = useState<string | null>(null)

	const connectWallet = useCallback(async () => {
		if (isPhantomExtensionLoading) {
			throw new Error('Checking Phantom extension status...')
		}

		if (isPhantomExtensionInstalled) {
			await connectPhantom({ provider: 'injected' })
			return
		}

		openPhantomModal()
	}, [connectPhantom, isPhantomExtensionInstalled, isPhantomExtensionLoading, openPhantomModal])

	const ensureWalletReady = useCallback(async () => {
		if (!currency || !adminAddress) {
			throw new Error('Deposit details are incomplete.')
		}

		if (!isSolanaNetwork(currency.network)) {
			throw new Error(`Unsupported network: ${currency.network}`)
		}

		if (!isAvailable) {
			throw new Error('Solana is not available in the connected wallet.')
		}

		if (!solana.isConnected) {
			await connectWallet()
		}

		if (!solana.publicKey) {
			throw new Error('Phantom wallet is not connected.')
		}
	}, [adminAddress, connectWallet, currency, isAvailable, solana.isConnected, solana.publicKey])

	const payWithPhantom = useCallback(async () => {
		if (!currency || !adminAddress || !amount) {
			return
		}

		const mintAddress = currency.contract_address?.trim()
		if (mintAddress) {
			const message = `${currency.currency_code} SPL transfers are not supported yet.`
			setLastError(message)
			showApiErrorToast(message)
			return
		}

		setLastError(null)
		setIsSending(true)

		try {
			await ensureWalletReady()

			const fromAddress = solana.publicKey
			if (!fromAddress) {
				throw new Error('Phantom wallet is not connected.')
			}

			const connection = new Connection(getSolanaRpcUrl(currency.network))
			const { blockhash } = await connection.getLatestBlockhash()
			const parsedAmount = parseUnits(amount, currency.decimals)

			if (parsedAmount > BigInt(Number.MAX_SAFE_INTEGER)) {
				throw new Error('Deposit amount is too large.')
			}

			const transferInstruction = SystemProgram.transfer({
				fromPubkey: new PublicKey(fromAddress),
				toPubkey: new PublicKey(adminAddress),
				lamports: Number(parsedAmount),
			})

			const messageV0 = new TransactionMessage({
				payerKey: new PublicKey(fromAddress),
				recentBlockhash: blockhash,
				instructions: [transferInstruction],
			}).compileToV0Message()

			const transaction = new VersionedTransaction(messageV0)
			const result = await solana.signAndSendTransaction(transaction)
			const transactionId = result.signature?.trim()

			if (!transactionId) {
				throw new Error('Transaction hash was not returned.')
			}

			try {
				await depositPhantom({
					currency_code: currency.currency_code,
					transaction_id: transactionId,
					address: adminAddress,
					from: fromAddress,
				}).unwrap()
			} catch {
				setLastError('Unable to record Phantom deposit.')
				// Global API toasts are handled by playerBaseQuery.
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Unable to submit Phantom transaction.'
			setLastError(message)
			showApiErrorToast(message)
		} finally {
			setIsSending(false)
		}
	}, [adminAddress, amount, currency, depositPhantom, ensureWalletReady, solana])

	const isSubmitting = isPhantomConnecting || isSending || isRecordingDeposit

	return {
		walletAddress: solana.publicKey,
		isConnected: solana.isConnected,
		isAvailable,
		isSubmitting,
		lastError,
		payWithPhantom,
	}
}
